import { Stack } from '@mui/material';
import { colors, Button, ExpandableTypography } from '@sweep-io/sweep-design';
import { useCallback, useState, useEffect } from 'react';
import isEqual from 'lodash/isEqual';
import { ActionsMenu } from '../../../common/actions-menu/ActionsMenu';
import { Commands } from '../../../../types/enums/Common';
import EditableCardLayout from '../../../common/editable-card/EditableCardLayout';
import ConfirmDestructiveDialog from '../../../common/dialogs/ConfirmDestructiveDialog';
import useConfirm from '../../../common/dialogs/ConfirmLeaveWithoutSave/useConfirm';
import { TruncatedTextTooltip } from '../../../common/TruncatedTextTooltip';
import EditName from '../../../common/editable-card/EditName';
import EditTextarea from '../../../common/editable-card/EditTextarea';
import { getActionsMenu } from '../../../common/editable-card/utils';
import { AiSupportInstructionProperties } from '@server/ai';

type EditableInstructionCardProps = {
  card: AiSupportInstructionProperties;
  isDeleteAllowed: boolean;
  isUpdateAllowed: boolean;
  defaultEditMode?: boolean;
  onIsPristineChange?: (id: string, isPristine: boolean) => void;
  onUpdateOrCreate: (card: AiSupportInstructionProperties) => Promise<void>;
  onRemove: (id: string) => Promise<void>;
  onCancel?: () => void;
};

const EditableInstructionCard = ({
  card,
  isDeleteAllowed,
  isUpdateAllowed,
  defaultEditMode,
  onIsPristineChange,
  onUpdateOrCreate,
  onRemove,
  onCancel,
}: EditableInstructionCardProps) => {
  const [isEditMode, setIsEditMode] = useState(defaultEditMode ?? false);
  const [formData, setFormData] = useState<AiSupportInstructionProperties>(card);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { openConfirm, onCancel: _onCancel, onConfirm } = useConfirm();

  const actionsMenu = getActionsMenu(isDeleteAllowed, isUpdateAllowed);

  const validateForm = useCallback(() => {
    return formData.name && formData.content;
  }, [formData]);

  const isPristine = isEqual(card, formData);

  useEffect(() => {
    if (!isEditMode) return;
    const isPristine = isEqual(card, formData);

    onIsPristineChange?.(card.id, isPristine);

    return () => {
      onIsPristineChange?.(card.id, true);
    };
  }, [isEditMode, card, formData, onIsPristineChange]);

  const handleSave = useCallback(async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    if (!isPristine) {
      onUpdateOrCreate(formData);
    }
    setIsSubmitting(false);
    closeEditMode();
  }, [isPristine, validateForm, onUpdateOrCreate, formData]);

  const handleDeleteWithConfirmation = useCallback(
    async (id: string) => {
      const isConfirmed = await openConfirm(
        <ConfirmDestructiveDialog
          open
          onConfirm={onConfirm}
          onClose={_onCancel}
          title="Delete this custom instruction?"
          confirmText="Delete"
          cancelText="Cancel"
        >
          This will permanently remove the rule and all its details. This action can’t be undone.
        </ConfirmDestructiveDialog>,
      );

      if (isConfirmed) {
        await onRemove(id);
      }
    },
    [onRemove, openConfirm, _onCancel, onConfirm],
  );

  const handleActionClick = useCallback(
    (action: any) => {
      if (action.value === Commands.Delete && card.id) {
        handleDeleteWithConfirmation(card.id);
      }
      if (action.value === Commands.Edit) {
        setIsEditMode(true);
      }
    },
    [card, handleDeleteWithConfirmation],
  );

  const closeEditMode = () => {
    setIsEditMode(false);
  };

  const resetAndClose = () => {
    setFormData(card);
    closeEditMode();
    onCancel?.();
  };

  const nameJsx =
    isEditMode && !isSubmitting ? (
      <EditName
        setName={(newName: string) => setFormData((prev) => ({ ...prev, name: newName }))}
        name={formData.name}
        placeholder="Name this Instruction"
      />
    ) : (
      <TruncatedTextTooltip variant="h5">{formData.name}</TruncatedTextTooltip>
    );

  const contentJsx =
    isEditMode && !isSubmitting ? (
      <EditTextarea
        setText={(newText: string) => setFormData((prev) => ({ ...prev, content: newText }))}
        text={formData.content}
        placeholder="Describe what to do when a question involves a specific topic or keyword. Start with a phrase like “If the question is about…” and list the steps to follow."
      />
    ) : (
      <ExpandableTypography variant="body" color={colors.grey[700]} rows={7}>
        {formData.content}
      </ExpandableTypography>
    );

  const buttonsJsx = isEditMode ? (
    <Stack direction="row" gap={1} width="100%" justifyContent="flex-end">
      <Button variant="system" size="tiny" onClick={resetAndClose} disabled={isSubmitting}>
        Cancel
      </Button>
      <Button
        variant="primary"
        size="tiny"
        onClick={handleSave}
        loading={isSubmitting}
        disabled={!validateForm()}
      >
        Save
      </Button>
    </Stack>
  ) : undefined;

  const actionsJsx = isEditMode ? undefined : (
    <Stack direction="row" gap={1} alignItems="center">
      <ActionsMenu
        actions={actionsMenu}
        onClick={handleActionClick}
        iconSize="tiny"
        menuPlacement="left"
      />
    </Stack>
  );

  return (
    <EditableCardLayout
      borderColor={isEditMode ? colors.blue[600] : undefined}
      nameJsx={nameJsx}
      textJsx={contentJsx}
      updateCardActionsJsx={buttonsJsx}
      readCardActionsJsx={actionsJsx}
    />
  );
};

export default EditableInstructionCard;
